import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet-async";
import contactus from "../../Assets/contactus.jpg";
import { useSnackbar } from 'react-simple-snackbar'
import { useFormik } from "formik";
import * as Yup from "yup";
import { COLORS } from "../../Constants/colors";
import KEYWORDS from "../../Constants/keywords";
import {
  SubHeading,
  ParagraphLg as Paragraph,
  Divider,
} from "../../Constants/typography";

const Section = styled.div`
  padding: 0 100px 96px;
  box-sizing: border-box;
  @media screen and (max-width: 768px) {
    padding: 0 20px 56px;
  }
`;

const ContainerDiv = styled.div`
  padding-top: 112px;
  padding-bottom: 112px;
  box-sizing: border-box;
  @media screen and (max-width: 768px) {
    padding-top: 50px;
  }
`;

const Row = styled.div`
  display: flex;
  gap: 40px;
  box-sizing: border-box;
  @media screen and (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const Col = styled.div`
  color: #0e1014;
  box-sizing: border-box;
  @media screen and (max-width: 768px) {
    flex-basis: 100%;
  }
`;

const Col6 = styled(Col)`
  flex: 0 0 50%;
  font-size: 40px;
  font-weight: 300;
  @media screen and (max-width: 768px) {
    flex-basis: 100%;
  }
`;

const Heading = styled.div`
  font-size: 31px;
  color: #0e1014;
  line-height: 34px;
  font-weight: 500;
  @media screen and (max-width: 768px) {
    font-size: 20px;
    font-weight: 600;
  }
`;

const InputField = styled.input`
  background: ${COLORS.cardBackground};
  height: 56px;
  border-radius: 8px;
  padding: 16px;
  box-sizing: border-box;
  color: ${COLORS.textSecondary};
  font-size: 18px;
  border: 1px solid ${(props) => (props.error ? "red" : `${COLORS.mutedText}`)};
  width: 100%;
  font-family: "Inter", sans-serif;
  &::placeholder {
    color: ${COLORS.mutedText};
  }
  &:focus {
    outline: none;
    border-color: ${COLORS.primary}; /* or your desired focus color */
    box-shadow: 0 0 0 2px rgba(242, 110, 81, 0.2); /* optional soft glow */
  }
`;

const TextArea = styled.textarea`
  background: ${COLORS.cardBackground};
  height: 144px;
  border-radius: 8px;
  padding: 16px;
  box-sizing: border-box;
  color: ${COLORS.textSecondary};
  font-size: 18px;
  border: 1px solid ${(props) => (props.error ? "red" : `${COLORS.mutedText}`)};
  width: 100%;
  font-family: "Inter", sans-serif;
  &::placeholder {
    color: ${COLORS.mutedText};
  }
  &:focus {
    outline: none;
    border-color: ${COLORS.primary}; /* or your desired focus color */
    box-shadow: 0 0 0 2px rgba(242, 110, 81, 0.2); /* optional soft glow */
  }
`;

const Col12 = styled(Col)`
  flex: 0 0 100%;
  padding-top: 60px;
  padding-bottom: 60px;
  @media screen and (max-width: 768px) {
    flex-basis: 100%;
  }
`;

const Col4 = styled(Col)`
  padding-top: 16px;
  flex: 0 0 33.3333%;
  color: ${COLORS.textPrimary};
  font-size: 18px;
  line-height: 24px;
  font-weight: 500;
  @media screen and (max-width: 768px) {
    flex-basis: 100%;
    font-size: 14px;
    line-height: 15px;
  }
`;

const Col3 = styled(Col)`
  padding-top: 16px;
  flex: 0 0 25%;
  color:  ${COLORS.textPrimary};
  font-size: 18px;
  line-height: 24px;
  font-weight: 500;
  @media screen and (max-width: 768px) {
    flex-basis: 100%;
    font-size: 14px;
    line-height: 15px;
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  object-fit: cover;
  object-position: left;
  border-radius: 20px;
  @media screen and (max-width: 800px) {
    width: 300px;
    height: 228px;
    display: none;
  }
`;

const Button = styled.button`
  background: ${COLORS.primary};
  color: ${COLORS.buttonText};
  border-radius: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  height: 40px;
  font-size: 15px;
  letter-spacing: 0.5%;
  width: 121px;
  cursor: pointer;
  border: none;
  @media screen and (max-width: 768px) {
    width: 100%;
  }
`;

const ErrorMessage = styled.div`
  color: red;
  font-size: 14px;
  padding-top:0px;
`;

const ContactUsPage = () => {
  const isMobile = window.innerWidth <= 600;
  const errorSnackbarOptions = {
    position: 'bottom-center',
    style: {
      backgroundColor: `${COLORS.error}`,
      border: `2px solid ${COLORS.errorBorder}`,
      color: 'black',
      fontSize: isMobile ? '16px' : '24px',
      fontFamily: 'sans-serif',
      textAlign: 'center',
      borderRadius: '12px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
      padding: '16px',
      height: '100px',
      width: isMobile ? '90%' : '800px',
    },
    closeStyle: {
      color: 'black',
      fontSize: '20px',
    },
  };

  const successSnackbarOptions = {
    position: 'bottom-center',
    style: {
      backgroundColor: `${COLORS.success}`,
      border: `2px solid ${COLORS.successBorder}`,
      color: 'black',
      fontFamily: 'sans-serif',
      fontSize: isMobile ? '16px' : '24px',
      textAlign: 'center',
      borderRadius: '12px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
      height: '100px',
      width: isMobile ? '90%' : '800px',
    },
    closeStyle: {
      color: 'black',
      fontSize: '20px',
    },
  };
  
  const [openSuccessSnackbar, ] = useSnackbar(successSnackbarOptions);
  const [openErrorSnackbar, ] = useSnackbar(errorSnackbarOptions);
  const [isSubmitting, setIsSubmitting] = useState(false);


  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      phoneNumber: "",
      message: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      email: Yup.string().email("Invalid email address").required("Email is required"),
      phoneNumber: Yup.string()
        .matches(/^\d{10}$/, "Invalid phone number")
        .max(10, "Phone number must be exactly 10 characters")
        .nullable(),
      message: Yup.string().required("Message is required").max(100, "Message length should not be more than 100"),
    }),
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-functions-key": "i9XWaTqcnWds9QLkOHchUn67PppW_Hr9_-TR_OA9SfkAAzFuMI3ILw==",
        },
        body: JSON.stringify(values),
      };

      try {
        const response = await fetch("https://virtuosoftemail.azurewebsites.net/api/SendEmail", requestOptions);
        const data = await response.json();
        if (!response.ok || data.Status !== true) {
          throw new Error(data?.Message || "Failed to send email");
        }
        openSuccessSnackbar(`${data.Message || "Email sent successfully!"}`);
        
      } catch (error) {
        console.error("Error:", error.message);
        openErrorSnackbar(`${error.Message} || Something went wrong`);
        // Show error message in red snackbar
      } finally {
        resetForm();
        setIsSubmitting(false);
      }
    },
  });



  return (
    <>
      <Helmet>
        <meta name="keywords" content={KEYWORDS.contactus.join(", ")} />
      </Helmet>
      <Section>
        <ContainerDiv>
          <Row>
            <Col6>
                <div>
                  <SubHeading>Connect with our team</SubHeading>
                  <Divider/>
                </div>
              <form style={{ paddingTop: "32px" }} onSubmit={formik.handleSubmit}>
                <div style={{ gap: "24px" }}>
                  <InputField
                    placeholder="Your name"
                    name="name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.name && formik.errors.name}
                  />
                  {formik.touched.name && formik.errors.name && <ErrorMessage>{formik.errors.name}</ErrorMessage>}
                </div>
                <div style={{ paddingTop: "24px" }}>
                  <InputField
                    placeholder="Email address"
                    name="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.email && formik.errors.email}
                  />
                  {formik.touched.email && formik.errors.email && <ErrorMessage>{formik.errors.email}</ErrorMessage>}
                </div>
                <div style={{ paddingTop: "24px" }}>
                  <InputField
                    placeholder="Phone number"
                    name="phoneNumber"
                    value={formik.values.phoneNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.phoneNumber && formik.errors.phoneNumber}
                  />
                  {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                    <ErrorMessage>{formik.errors.phoneNumber}</ErrorMessage>
                  )}
                </div>
                <div style={{ paddingTop: "24px" }}>
                  <TextArea
                    placeholder="Your message"
                    name="message"
                    value={formik.values.message}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.message && formik.errors.message}
                  />
                  {formik.touched.message && formik.errors.message && (
                    <ErrorMessage>{formik.errors.message}</ErrorMessage>
                  )}
                </div>
                <div style={{ paddingTop: "24px" }}>
                  {/* <Button type="submit">Submit</Button> */}
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? "Submitting..." : "Submit"}
                  </Button>
                </div>
              </form>
            </Col6>
            <Col6>
              <Image src={contactus} alt="Contact-us"/>
            </Col6>
          </Row>
          <div style={{marginTop: "50px"}}>
            <SubHeading >{"CONTACT"}</SubHeading>
            <Divider/>
          </div>
          <Row>
            <Col4>
              {"Virtuosoft Technologies Private Limited, "}
              <br />
              {"3rd floor, Amstor House, "}
              <br />
              {"Technopark Phase 1, Kazhakkoottam, Trivandrum, Kerala 695582"}
            </Col4>
            {/* <Col1/> */}
            <Col3>
              {"Email"}
              <br />
              {"contactus@virtuosoft.in​"}
            </Col3>
            {/* <Col1/> */}
            <Col3>
              {/* {"Linkedin"} */}
              <a  style={{
                    cursor:'pointer',
                    textDecoration:'none',
                    color: `${COLORS.textPrimary}`,
                  }} href="https://www.linkedin.com/company/virtuosoft" target="_blank" rel="noreferrer">
                  {"LinkedIn"}</a>
              <br />
            </Col3>
          </Row>
          <Row>
            {/* <Col4>
            {'CONTACT'}
            </Col4> */}
          </Row>
          <Row>
            <Col12>
              {/* <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3945.409416858738!2d76.87940767586271!3d8.556570796203403!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3b05bf00574ef1e5%3A0xb87788f6b313392c!2sVirtuosoft%20Technologies%20Private%20Limited!5e0!3m2!1sen!2sin!4v1728310757152!5m2!1sen!2sin"
                  width="100%"
                  height="705"
                  style={{ border: 0, borderRadius: '30px' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Virtuosoft Map"
                ></iframe> */}
                <iframe src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d986.3545889114009!2d76.8801245941429!3d8.555708000000003!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3b05bf00574ef1e5%3A0xb87788f6b313392c!2sVirtuosoft%20Technologies%20Private%20Limited!5e0!3m2!1sen!2sin!4v1753084411547!5m2!1sen!2sin"
                  width="100%"
                  height="705"
                  style={{ border: 0, borderRadius: '30px' }}
                  allowfullscreen
                  loading="lazy" 
                  referrerpolicy="no-referrer-when-downgrade"
                ></iframe>
            </Col12>
          </Row>
        </ContainerDiv>
      </Section>
    </>
  );
};

export default ContactUsPage;
